import React from "react";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import { useGetUserProfile } from "@/hooks/useGetUserProfile";
import { useGetUserLevel } from "@/hooks/useGetUserLevel";
import { useGetTransactionsPaginated } from "@/hooks/useGetTransactionsPaginated";
import { FaFire } from "react-icons/fa";
import "../style/components/_compact-profile-bar.scss";

/**
 * Compact Profile Bar for in-game overlay mode
 * Shows minimal user stats: Level, XP, and Fuel balance
 */
const CompactProfileBar: React.FC = () => {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const currentUser = useSelector((store: IStore) => store.metaStore.currentUser);
    const accountLevel = useSelector((store: IStore) => store.metaStore.accountLevel);

    // Fetch user data
    const { data: userProfile } = useGetUserProfile(fullToken);
    const { data: userLevelData } = useGetUserLevel(fullToken, currentUser?.user?.id);
    const { data: transactionsData } = useGetTransactionsPaginated(fullToken, currentUser?.user?.id);

    // Use accountLevel from Redux store (updated by other components) or fallback to API data
    const level = accountLevel?.level || userLevelData?.level || userProfile?.user?.level || 1;
    const experienceGained = accountLevel?.experienceGainedAtThisLevel || userLevelData?.experienceGainedAtThisLevel || 0;
    const experienceRequired = accountLevel?.experienceRequiredForNextLevel || userLevelData?.experienceRequiredForNextLevel || 1;
    const totalExperience = experienceGained + experienceRequired;

    // Format fuel balance
    const fuelBalance = transactionsData
        ? Intl.NumberFormat("en-us", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(transactionsData.balanceInUsd)
        : "0";

    // Don't render if not logged in
    if (!fullToken) {
        return null;
    }

    return (
        <div className="compact-profile-bar">
            <div className="compact-profile-bar__stats">
                <div className="compact-profile-bar__stat">
                    <span className="compact-profile-bar__stat-label">Lv.</span>
                    <span className="compact-profile-bar__stat-value">{level}</span>
                </div>

                <div className="compact-profile-bar__stat">
                    <span className="compact-profile-bar__stat-value">
                        {experienceGained.toLocaleString()}
                    </span>
                    <span className="compact-profile-bar__stat-label">
                        / {totalExperience.toLocaleString()} XP
                    </span>
                </div>

                <div className="compact-profile-bar__stat compact-profile-bar__stat--fuel">
                    <FaFire className="compact-profile-bar__fuel-icon" />
                    <span className="compact-profile-bar__stat-value">${fuelBalance}</span>
                </div>
            </div>
        </div>
    );
};

export default CompactProfileBar;